import React, { FunctionComponent, useEffect, useState } from 'react';
import AppContextProps from '../../contexts/AppContextProps';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import {
  Alert,
  Box,
  Button,
  Grid,
  Header,
  Input,
  SpaceBetween,
  Table,
} from '@amzn/awsui-components-core';
import {
  AuthorPermission,
  ContentAccessUser,
  ContentType,
  UserProfile,
} from '@amzn/awscat-pattern-library-service-model';
import UserIdAutosuggest, { AdditionlUserInfo, SearchUserProfile } from './UserIdAutosuggest';
import { IMplServiceClient } from '../../api/IMplServiceClient';
import ModalDialogButtonV3 from './ModalDialogButtonV3';

interface Props extends AppContextProps {
  owner: UserProfile;
  coAuthors: ContentAccessUser[] | undefined;
  peerReviewers: ContentAccessUser[] | undefined;
  currentUser: UserProfile;
  contentType: ContentType;
  contentId: string;
}

interface AuthorItem {
  userId: string;
  userName: string;
  permission: AuthorPermission;
}

export const AuthorsList: FunctionComponent<Props> = (props) => {
  const mplServiceClient: IMplServiceClient = MplServiceClientFactory.create();
  const owner = props.owner;
  const coAuthors = props.coAuthors;
  const [authorItems, setAuthorItems] = useState<AuthorItem[]>([]);

  const [selectedUser, setSelectedUser] = useState<SearchUserProfile>();
  const [disableAddAuthor, setDisableAddAuthor] = useState<boolean>(true);
  const [isAddingAuthor, setIsAddingAuthor] = useState<boolean>(false);
  const [confirmText, setConfirmText] = useState<string>('');
  const [disableRemoveAuthor, setDisableRemoveAuthor] = useState<boolean>(true);
  const [displayAlert, setDisplayAlert] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>('');

  useEffect(() => {
    const ownerItem: AuthorItem = {
      userId: owner?.userId ?? '',
      userName: owner?.name ?? '',
      permission: AuthorPermission.AUTHOR,
    };

    setAuthorItems([
      ownerItem,
      ...(coAuthors?.map((ca) => ({
        userId: ca.userId,
        userName: ca.userName,
        permission: AuthorPermission.COAUTHOR,
      })) ?? []),
    ]);
  }, [coAuthors, owner?.userId, owner?.name]);

  // @ts-expect-error TS7006 - strict migration
  const handleDeleteCoauthor = async (userId) => {
    await mplServiceClient.unShareContents([props.contentId], [userId]);
    setAuthorItems(authorItems.filter((a) => a.userId !== userId));
  };

  const handleUserSelected = (
    selectedUserId: string,
    selectedUserName: string,
    validationError: string,
    additionalInfo: AdditionlUserInfo,
  ) => {
    setDisplayAlert(false);
    setSelectedUser({
      userId: selectedUserId,
      name: selectedUserName,
      email: additionalInfo?.email || '',
    });
    if (validationError) {
      setDisableAddAuthor(true);
    } else {
      setDisableAddAuthor(false);
    }
  };

  const updateConfirmText = (newString: string) => {
    setConfirmText(newString);
    if (newString === 'confirm') {
      setDisableRemoveAuthor(false);
    } else {
      setDisableRemoveAuthor(true);
    }
  };

  // @ts-expect-error TS7006 - strict migration
  const renderDeleteCoauthorButton = (authorItem) => {
    return (
      <ModalDialogButtonV3
        variant="icon"
        iconUrl={'/images/icon-delete.svg'}
        header={'Remove author'}
        submitButtonText={'Remove'}
        onSubmit={() => handleDeleteCoauthor(authorItem.userId)}
        disabled={false}
        onDismiss={() => setConfirmText('')}
        fullwidth
        inputValid={!disableRemoveAuthor}
      >
        Remove <b>{authorItem.userName}</b> from {props.contentType}?
        <br />
        <br />
        To confirm this removal, type "confirm"
        <Input
          value={confirmText}
          onChange={({ detail }) => updateConfirmText(detail.value)}
          placeholder="confirm"
          ariaRequired
        />
      </ModalDialogButtonV3>
    );
  };

  const handleDismissAlert = () => {
    setDisplayAlert(false);
    setAlertMessage('');
  };

  const alert = (
    <Alert type="error" dismissible onDismiss={handleDismissAlert} header={alertMessage} />
  );

  const handleAddAuthor = async () => {
    setDisplayAlert(false);
    const { userId, name } = selectedUser ?? {};

    if (!userId || !name) {
      setDisableAddAuthor(true);
      return;
    } else if (authorItems.some((aId) => aId.userId === userId)) {
      setAlertMessage('This user is already an author.');
      setDisplayAlert(true);
      setDisableAddAuthor(true);
    } else if (props.peerReviewers?.some((pr) => pr.userId === userId)) {
      setAlertMessage('Cannot add this user as a co-author, they are already a peer reviewer.');
      setDisplayAlert(true);
      setDisableAddAuthor(true);
    } else {
      setIsAddingAuthor(true);
      await mplServiceClient.shareContents([props.contentId], [userId]);
      setAuthorItems([
        ...authorItems,
        {
          userId,
          userName: name,
          permission: AuthorPermission.COAUTHOR,
        },
      ]);
      setSelectedUser(undefined);
      setIsAddingAuthor(false);
      setDisableAddAuthor(true);
    }
  };

  const searchAndAddAuthor = () => {
    return (
      <Grid gridDefinition={[{ colspan: 5 }, { colspan: 7 }]}>
        <UserIdAutosuggest onUserSelected={handleUserSelected} />
        <Button onClick={handleAddAuthor} disabled={disableAddAuthor} loading={isAddingAuthor}>
          Add co-author
        </Button>
      </Grid>
    );
  };

  return (
    <div>
      {displayAlert && alert}
      <Table
        columnDefinitions={[
          {
            id: 'name',
            header: 'Name',
            cell: (e) => e.userName,
            width: 600,
            minWidth: 200,
            sortingField: 'name',
            isRowHeader: true,
          },
          {
            id: 'permission',
            header: 'Permission',
            cell: (e) => e.permission,
            width: 400,
            minWidth: 200,
            sortingField: 'permission',
          },
          {
            id: 'action',
            header: 'Action',
            cell: (e) =>
              e.permission === AuthorPermission.AUTHOR || e.userId === props.currentUser.userId ? (
                <div> - -</div>
              ) : (
                renderDeleteCoauthorButton(e)
              ),
          },
        ]}
        enableKeyboardNavigation
        items={authorItems}
        loadingText="Loading authors"
        resizableColumns
        empty={
          <Box margin={{ vertical: 'xs' }} textAlign="center" color="inherit">
            <SpaceBetween size="m">
              <b>No authors</b>
            </SpaceBetween>
          </Box>
        }
        header={<Header variant="h3">Authors</Header>}
        filter={searchAndAddAuthor()}
      />
    </div>
  );
};
